import React, { useState, useEffect, useRef, useCallback } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import { notesService } from '../../services/notesService';
import { useAuth } from '../../contexts/AuthContext';
import MobilePhotoInput from './MobilePhotoInput';
import NoteDeleteConfirmModal from './NoteDeleteConfirmModal';

// ─── Visibility config ────────────────────────────────────────────────────────
const VISIBILITY_OPTIONS = [
  {
    value: 'private',
    label: 'Private',
    description: 'Visible to Admin only',
    icon: 'Lock',
    badgeClass: 'bg-red-100 text-red-700 border-red-200',
    buttonActive: 'bg-red-50 border-red-300 text-red-700',
  },
  {
    value: 'team',
    label: 'Team',
    description: 'Visible to Admin, Site Manager & Workers',
    icon: 'Users',
    badgeClass: 'bg-amber-100 text-amber-700 border-amber-200',
    buttonActive: 'bg-amber-50 border-amber-300 text-amber-700',
  },
  {
    value: 'client_shared',
    label: 'Client Shared',
    description: 'Visible to all including Client Portal',
    icon: 'Globe',
    badgeClass: 'bg-blue-100 text-blue-700 border-blue-200',
    buttonActive: 'bg-blue-50 border-blue-300 text-blue-700',
  },
];

const getVisibilityConfig = (value) =>
  VISIBILITY_OPTIONS?.find(o => o?.value === value) || VISIBILITY_OPTIONS?.[1];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatDateTime = (isoString) => {
  if (!isoString) return '';
  const d = new Date(isoString);
  return (
    d?.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) +
    ' at '+ d?.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  );
};

// ─── VisibilityBadge ──────────────────────────────────────────────────────────
const VisibilityBadge = ({ visibility }) => {
  const cfg = getVisibilityConfig(visibility);
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg?.badgeClass}`}>
      <Icon name={cfg?.icon} size={10} />
      {cfg?.label}
    </span>
  );
};

// ─── MentionText — renders @mentions as highlighted spans ─────────────────────
const MentionText = ({ text }) => {
  if (!text) return null;
  const parts = text?.split(/(@\w[\w\s]*)/g);
  return (
    <span>
      {parts?.map((part, i) =>
        part?.startsWith('@') ? (
          <span key={i} className="text-accent font-medium">{part}</span>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </span>
  );
};

// ─── MentionsPicker ───────────────────────────────────────────────────────────
const MentionsPicker = ({ users, query, onSelect, onClose }) => {
  const filtered = users?.filter(u =>
    u?.full_name?.toLowerCase()?.includes(query?.toLowerCase())
  )?.slice(0, 8);

  if (!filtered?.length) return null;

  return (
    <div className="absolute z-50 bottom-full mb-1 left-0 w-64 bg-card border border-border rounded-lg shadow-lg overflow-hidden">
      <div className="px-3 py-2 border-b border-border bg-muted/30">
        <p className="text-xs font-medium text-muted-foreground">Mention a team member</p>
      </div>
      <ul className="max-h-48 overflow-y-auto">
        {filtered?.map(user => (
          <li key={user?.id}>
            <button
              type="button"
              onClick={() => onSelect(user)}
              className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-muted/50 transition-colors text-left"
            >
              <div className="w-7 h-7 rounded-full bg-accent/10 flex items-center justify-center text-accent font-semibold text-xs flex-shrink-0">
                {user?.full_name?.charAt(0)?.toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{user?.full_name}</p>
                <p className="text-xs text-muted-foreground capitalize">{user?.role?.replace('_', ' ')}</p>
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

// ─── NoteCard ─────────────────────────────────────────────────────────────────
const NoteCard = ({ note, canDelete, canEdit, onDelete, onEdit }) => {
  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [photoExpanded, setPhotoExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(note?.note_text || '');
  const [editVisibility, setEditVisibility] = useState(note?.visibility || 'team');
  const [saving, setSaving] = useState(false);

  const authorName = note?.creator?.full_name || note?.created_by_name || 'Unknown';
  const authorRole = note?.creator?.role;
  const roleLabel =
    authorRole === 'admin' ? 'Admin'
    : authorRole === 'site_manager' ? 'Site Manager'
    : authorRole === 'worker'? 'Worker' :'';

  // NSherjFh PR 11 — open the confirm modal. The actual delete runs
  // from the modal's onConfirm so users can back out cleanly. Modal
  // copy emphasises hard-delete (no recovery) to distinguish from
  // PRs 2-10 soft-delete patterns.
  const handleDeleteClick = () => setConfirmOpen(true);
  const handleConfirmDelete = async () => {
    setDeleting(true);
    await onDelete(note?.id);
    setDeleting(false);
    setConfirmOpen(false);
  };

  const handleSaveEdit = async () => {
    if (!editText?.trim()) return;
    setSaving(true);
    await onEdit(note?.id, { noteText: editText?.trim(), visibility: editVisibility, mentions: note?.mentions || [] });
    setSaving(false);
    setEditing(false);
  };

  return (
    <div className="flex gap-3 group">
      {/* Avatar */}
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center text-accent font-semibold text-sm">
        {authorName?.charAt(0)?.toUpperCase()}
      </div>

      <div className="flex-1 min-w-0">
        <div className="bg-card border border-border rounded-lg p-3 shadow-sm">
          {/* Header */}
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex flex-wrap items-center gap-2 min-w-0">
              <span className="text-sm font-semibold text-foreground truncate">{authorName}</span>
              {roleLabel && (
                <span className="text-xs text-muted-foreground">({roleLabel})</span>
              )}
              <VisibilityBadge visibility={note?.visibility || 'team'} />
            </div>
            <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
              {canEdit && !editing && (
                <button
                  onClick={() => { setEditing(true); setEditText(note?.note_text); setEditVisibility(note?.visibility || 'team'); }}
                  className="w-7 h-7 rounded flex items-center justify-center text-muted-foreground hover:text-accent hover:bg-accent/10"
                  title="Edit note"
                >
                  <Icon name="Pencil" size={13} />
                </button>
              )}
              {canDelete && !editing && (
                <button
                  onClick={handleDeleteClick}
                  disabled={deleting}
                  className="w-7 h-7 rounded flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                  title="Delete note"
                >
                  {deleting
                    ? <Icon name="Loader2" size={13} className="animate-spin" />
                    : <Icon name="Trash2" size={13} />
                  }
                </button>
              )}
            </div>
          </div>

          {/* Note text or edit form */}
          {editing ? (
            <div className="space-y-2">
              <textarea
                value={editText}
                onChange={e => setEditText(e?.target?.value)}
                rows={3}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent resize-none"
              />
              {/* Visibility selector in edit mode */}
              <div className="flex gap-1.5 flex-wrap">
                {VISIBILITY_OPTIONS?.map(opt => (
                  <button
                    key={opt?.value}
                    type="button"
                    onClick={() => setEditVisibility(opt?.value)}
                    className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
                      editVisibility === opt?.value ? opt?.buttonActive : 'bg-background border-border text-muted-foreground hover:border-accent/50'
                    }`}
                  >
                    <Icon name={opt?.icon} size={10} />
                    {opt?.label}
                  </button>
                ))}
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  className="px-3 py-1.5 text-xs rounded-lg border border-border text-muted-foreground hover:bg-muted transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveEdit}
                  disabled={saving || !editText?.trim()}
                  className="px-3 py-1.5 text-xs rounded-lg bg-accent text-white hover:bg-accent/90 disabled:opacity-50 transition-colors flex items-center gap-1"
                >
                  {saving ? <Icon name="Loader2" size={11} className="animate-spin" /> : <Icon name="Check" size={11} />}
                  Save
                </button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">
              <MentionText text={note?.note_text} />
            </p>
          )}

          {/* Mentions */}
          {!editing && note?.mentions?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {note?.mentions?.map(m => (
                <span key={m?.id} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-accent/10 text-accent">
                  <Icon name="AtSign" size={9} />
                  {m?.name}
                </span>
              ))}
            </div>
          )}

          {/* Photo attachment */}
          {!editing && note?.photo_url && (
            <div className="mt-3">
              <button
                onClick={() => setPhotoExpanded(!photoExpanded)}
                className="flex items-center gap-1.5 text-xs text-accent hover:underline"
              >
                <Icon name="Paperclip" size={12} />
                {photoExpanded ? 'Hide photo' : 'View attached photo'}
              </button>
              {photoExpanded && (
                <div className="mt-2 rounded-lg overflow-hidden border border-border max-w-xs">
                  <img
                    src={note?.photo_url}
                    alt="Note attachment"
                    className="w-full object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Timestamp */}
        <p className="text-xs text-muted-foreground mt-1 ml-1">
          {formatDateTime(note?.created_at)}
          {note?.updated_at && note?.updated_at !== note?.created_at && (
            <span className="ml-1 italic">(edited)</span>
          )}
        </p>
      </div>

      {/* NSherjFh PR 11 — hard-delete confirm modal. */}
      <NoteDeleteConfirmModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        isDeleting={deleting}
      />
    </div>
  );
};

// ─── NotesFeed (main component) ───────────────────────────────────────────────
/**
 * NotesFeed — reusable notes component for Sites, Tasks, Snags, Incidents, Near Misses.
 *
 * Props:
 *   entityType: 'site' | 'task' | 'snag' | 'incident' | 'near_miss'
 *   entityId: UUID
 *   companyId: UUID
 *   title: string (optional, defaults to "Notes")
 */
const NotesFeed = ({ entityType, entityId, companyId, title = 'Notes' }) => {
  const { userProfile } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [visibility, setVisibility] = useState('team');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [error, setError] = useState('');
  const [siteUsers, setSiteUsers] = useState([]);
  const [mentionQuery, setMentionQuery] = useState('');
  const [showMentionPicker, setShowMentionPicker] = useState(false);
  const [selectedMentions, setSelectedMentions] = useState([]);
  const feedEndRef = useRef(null);
  const textareaRef = useRef(null);

  const role = userProfile?.role;
  const isAdmin = role === 'admin';
  const isAdminOrManager = ['admin', 'site_manager']?.includes(role);
  const canAddNote = ['admin', 'site_manager', 'worker']?.includes(role);
  // NSherjFh PR 11 — Delete UI mirrors the actual DELETE RLS:
  // admin_manager_can_delete_notes (migration 20260328080000) restricts
  // to admin + site_manager. The earlier "creator or admin" policy from
  // migration 20260328070000 was tightened to admin/SM only 10 minutes
  // later in the same wave, so workers cannot delete via RLS. Workers
  // can still edit their own notes (creator_or_admin_manager_can_
  // update_notes policy), just not delete them — consistent with the
  // umbrella's preference for restricting destructive actions even
  // when source authorship would suggest broader access. Hard-delete
  // with no recovery, per Heidi's 28 May call: notes are ephemeral
  // scratchpad records with no compliance value.
  const canDeleteNote = (note) => isAdminOrManager;
  const canEditNote = (note) => isAdminOrManager || note?.created_by === userProfile?.id;

  // Workers can only create team or client_shared notes (not private)
  const availableVisibilities = isAdmin
    ? VISIBILITY_OPTIONS
    : isAdminOrManager
    ? VISIBILITY_OPTIONS?.filter(o => o?.value !== 'private')
    : VISIBILITY_OPTIONS?.filter(o => o?.value === 'team' || o?.value === 'client_shared');

  useEffect(() => {
    if (entityId && companyId) {
      loadNotes();
      loadSiteUsers();
    }
  }, [entityId, companyId]);

  const loadNotes = async () => {
    setLoading(true);
    const result = await notesService?.getNotes(entityType, entityId, role);
    if (result?.data) setNotes(result?.data);
    setLoading(false);
  };

  const loadSiteUsers = async () => {
    if (!companyId) return;
    const result = await notesService?.getSiteUsers(entityId, companyId);
    if (result?.data) setSiteUsers(result?.data);
  };

  const handlePhotoSelect = (files) => {
    const file = files?.[0];
    if (!file) return;
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleRemovePhoto = () => {
    setPhotoFile(null);
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoPreview(null);
  };

  // Handle @mention typing in textarea
  const handleTextChange = (e) => {
    const val = e?.target?.value;
    setNoteText(val);

    // Detect @ trigger
    const cursorPos = e?.target?.selectionStart;
    const textUpToCursor = val?.substring(0, cursorPos);
    const atMatch = textUpToCursor?.match(/@(\w*)$/);
    if (atMatch) {
      setMentionQuery(atMatch?.[1]);
      setShowMentionPicker(true);
    } else {
      setShowMentionPicker(false);
      setMentionQuery('');
    }
  };

  const handleMentionSelect = (user) => {
    const cursorPos = textareaRef?.current?.selectionStart || noteText?.length;
    const textUpToCursor = noteText?.substring(0, cursorPos);
    const textAfterCursor = noteText?.substring(cursorPos);
    const atIndex = textUpToCursor?.lastIndexOf('@');
    const newText = textUpToCursor?.substring(0, atIndex) + `@${user?.full_name} ` + textAfterCursor;
    setNoteText(newText);
    setShowMentionPicker(false);
    setMentionQuery('');
    // Add to selected mentions if not already there
    setSelectedMentions(prev => {
      if (prev?.some(m => m?.id === user?.id)) return prev;
      return [...prev, { id: user?.id, name: user?.full_name }];
    });
    textareaRef?.current?.focus();
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!noteText?.trim()) return;
    if (!companyId) {
      setError('Company context missing — cannot save note.');
      return;
    }

    setSubmitting(true);
    setError('');

    const result = await notesService?.addNote({
      entityType,
      entityId,
      noteText: noteText?.trim(),
      visibility,
      photoFile: photoFile || null,
      companyId,
      mentions: selectedMentions,
    });

    if (result?.error) {
      setError(result?.error?.message || 'Failed to save note. Please try again.');
    } else if (result?.data) {
      setNotes(prev => [...prev, result?.data]);
      setNoteText('');
      setVisibility('team');
      setSelectedMentions([]);
      handleRemovePhoto();
      setTimeout(() => feedEndRef?.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }

    setSubmitting(false);
  };

  const handleDelete = async (noteId) => {
    const result = await notesService?.deleteNote(noteId);
    if (result?.error) {
      // Surface the real failure and leave the note in the UI so the user
      // can retry. Removing it locally on a failed delete diverges UI from
      // server state.
      const err = result?.error;
      console.error('[NotesFeed] delete failed:', { noteId, code: err?.code, message: err?.message });
      if (err?.code === 'PGRST301' || err?.code === '42501') {
        setError(`Permission denied deleting note: ${err?.message ?? `code ${err?.code}`}`);
      } else {
        setError(`Failed to delete note: ${err?.message ?? 'unknown error'}`);
      }
      return;
    }
    setNotes(prev => prev?.filter(n => n?.id !== noteId));
  };

  const handleEdit = async (noteId, updates) => {
    const result = await notesService?.editNote(noteId, updates);
    if (result?.error) {
      const err = result?.error;
      console.error('[NotesFeed] edit failed:', { noteId, code: err?.code, message: err?.message });
      if (err?.code === 'PGRST301' || err?.code === '42501') {
        setError(`Permission denied editing note: ${err?.message ?? `code ${err?.code}`}`);
      } else {
        setError(`Failed to edit note: ${err?.message ?? 'unknown error'}`);
      }
      return;
    }
    if (result?.data) {
      setNotes(prev => prev?.map(n => n?.id === noteId ? result?.data : n));
    }
  };

  const visibilityCfg = getVisibilityConfig(visibility);

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <Icon name="MessageSquare" size={16} className="text-accent" />
          {title}
          {notes?.length > 0 && (
            <span className="px-1.5 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
              {notes?.length}
            </span>
          )}
        </h3>
        {role === 'client' && (
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <Icon name="Globe" size={12} />
            Client Shared notes only
          </span>
        )}
        {role === 'worker' && (
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <Icon name="Users" size={12} />
            Team &amp; Client Shared notes
          </span>
        )}
      </div>

      {/* Feed */}
      <div className="px-4 py-4 space-y-4 max-h-96 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Icon name="Loader2" size={24} className="animate-spin text-accent" />
          </div>
        ) : notes?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="MessageSquare" size={32} className="text-muted-foreground/40 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No notes yet</p>
            <p className="text-xs text-muted-foreground mt-1">Add the first note below</p>
          </div>
        ) : (
          notes?.map(note => (
            <NoteCard
              key={note?.id}
              note={note}
              canDelete={canDeleteNote(note)}
              canEdit={canEditNote(note)}
              onDelete={handleDelete}
              onEdit={handleEdit}
            />
          ))
        )}
        <div ref={feedEndRef} />
      </div>

      {/* Add note form */}
      {canAddNote && (
        <div className="border-t border-border px-4 py-4">
          <form onSubmit={handleSubmit} className="space-y-3">

            {/* Visibility selector */}
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1.5">Visibility</p>
              <div className="flex gap-2 flex-wrap">
                {availableVisibilities?.map(opt => (
                  <button
                    key={opt?.value}
                    type="button"
                    onClick={() => setVisibility(opt?.value)}
                    className={`flex-1 min-w-0 py-2 px-2.5 rounded-lg border text-xs font-medium transition-all ${
                      visibility === opt?.value ? opt?.buttonActive : 'bg-background border-border text-muted-foreground hover:border-accent/50'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-1.5">
                      <Icon name={opt?.icon} size={12} />
                      {opt?.label}
                    </div>
                    <div className="text-xs font-normal mt-0.5 opacity-70 hidden sm:block leading-tight">{opt?.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Text area with @mention support */}
            <div className="relative">
              <textarea
                ref={textareaRef}
                value={noteText}
                onChange={handleTextChange}
                onKeyDown={(e) => {
                  if (e?.key === 'Escape') setShowMentionPicker(false);
                }}
                placeholder={`Add a ${visibilityCfg?.label?.toLowerCase()} note… Type @ to mention a team member`}
                rows={3}
                className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent resize-none"
              />
              {showMentionPicker && siteUsers?.length > 0 && (
                <MentionsPicker
                  users={siteUsers}
                  query={mentionQuery}
                  onSelect={handleMentionSelect}
                  onClose={() => setShowMentionPicker(false)}
                />
              )}
            </div>

            {/* Selected mentions chips */}
            {selectedMentions?.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {selectedMentions?.map(m => (
                  <span key={m?.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-accent/10 text-accent border border-accent/20">
                    <Icon name="AtSign" size={10} />
                    {m?.name}
                    <button
                      type="button"
                      onClick={() => setSelectedMentions(prev => prev?.filter(x => x?.id !== m?.id))}
                      className="ml-0.5 hover:text-destructive"
                    >
                      <Icon name="X" size={10} />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Photo attachment */}
            {photoPreview ? (
              <div className="flex items-center gap-3 p-2 bg-muted/50 rounded-lg border border-border">
                <img src={photoPreview} alt="Attachment preview" className="w-12 h-12 object-cover rounded-md border border-border" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-foreground truncate">{photoFile?.name}</p>
                  <p className="text-xs text-muted-foreground">{(photoFile?.size / 1024)?.toFixed(0)} KB</p>
                </div>
                <button
                  type="button"
                  onClick={handleRemovePhoto}
                  className="w-7 h-7 rounded flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                >
                  <Icon name="X" size={14} />
                </button>
              </div>
            ) : (
              <MobilePhotoInput
                onFilesSelected={handlePhotoSelect}
                compact={true}
                label="Attach Photo (optional)"
                hint="Attach a photo to this note"
              />
            )}

            {/* Error */}
            {error && (
              <div className="flex items-center gap-2 p-2 bg-destructive/10 border border-destructive/20 rounded-lg text-xs text-destructive">
                <Icon name="AlertCircle" size={14} className="flex-shrink-0" />
                {error}
              </div>
            )}

            {/* Submit */}
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Icon name={visibilityCfg?.icon} size={11} />
                {visibilityCfg?.description}
              </p>
              <Button
                type="submit"
                variant="primary"
                size="sm"
                disabled={!noteText?.trim() || submitting}
                iconName={submitting ? 'Loader2' : 'Send'}
              >
                {submitting ? 'Saving…' : 'Add Note'}
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default NotesFeed;
